import React, { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Button } from "antd";
import { BellOutlined } from "@ant-design/icons";
import {
  getPatientNotifications,
  getPatientNotificationUnreadCount,
  markPatientNotificationsRead,
  markPatientNotificationRead,
} from "../services/patientService";
import NotificationPanel from "./NotificationPanel";

const POLL_MS = 30_000;

const APPT_TYPES = new Set([
  "APPOINTMENT_BOOKED", "APPOINTMENT_CONFIRMED",
  "APPOINTMENT_REJECTED", "APPOINTMENT_CANCELLED",
]);
const RX_TYPES = new Set(["PRESCRIPTION_STATUS_CHANGE"]);
const SUPPORT_TYPES = new Set([
  "SUPPORT_TICKET_RESPONSE", "USER_TICKET", "ADMIN_RESPONSE",
]);

const PATIENT_TABS = [
  { key: "all",           label: "All",           filter: null },
  { key: "unread",        label: "Unread",         filter: n => !n.is_read },
  { key: "appointments",  label: "Appointments",   filter: n => APPT_TYPES.has(n.notification_type) },
  { key: "prescriptions", label: "Prescriptions",  filter: n => RX_TYPES.has(n.notification_type) },
  { key: "support",       label: "Support",        filter: n => SUPPORT_TYPES.has(n.notification_type) },
];

export default function PatientNotificationBell() {
  const [unread,  setUnread]  = useState(0);
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [open,    setOpen]    = useState(false);
  const pollRef = useRef();
  const openRef = useRef(false);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await getPatientNotificationUnreadCount();
      setUnread(res.data?.unread_count ?? 0);
    } catch { /* silent */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPatientNotifications();
      setItems(Array.isArray(res.data) ? res.data : (res.data?.results ?? []));
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchUnread();
    pollRef.current = setInterval(() => {
      fetchUnread();
      if (openRef.current) fetchList();
    }, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchUnread, fetchList]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpen = () => {
    const next = !open;
    openRef.current = next;
    setOpen(next);
    if (next) fetchList();
  };

  const handleClose = () => {
    openRef.current = false;
    setOpen(false);
  };

  const handleItemClick = async (item) => {
    handleClose();
    if (!item.is_read) {
      try {
        await markPatientNotificationRead(item.id);
        setItems(prev => prev.map(n => n.id === item.id ? { ...n, is_read: true } : n));
        setUnread(c => Math.max(0, c - 1));
      } catch { /* silent */ }
    }
  };

  const handleMarkAll = async () => {
    try {
      await markPatientNotificationsRead();
      setItems(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnread(0);
    } catch { /* silent */ }
  };

  const badgeCount = unread > 99 ? "99+" : unread;

  return (
    <>
      <Badge count={badgeCount} size="small" offset={[-2, 4]} style={{ backgroundColor: "#52c41a" }}>
        <Button
          type="text"
          shape="circle"
          icon={<BellOutlined style={{ fontSize: 20 }} />}
          aria-label="Notifications"
          onClick={handleOpen}
        />
      </Badge>

      <NotificationPanel
        visible={open}
        onClose={handleClose}
        notifications={items}
        unreadCount={unread}
        loading={loading}
        onMarkAllRead={handleMarkAll}
        onItemClick={handleItemClick}
        tabsConfig={PATIENT_TABS}
      />
    </>
  );
}
